import { useState, useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, PlusCircle, Briefcase, CalendarDays, LogOut, BarChart3, Building2, Settings, Key, Menu, X, FileBarChart, Bot, Users, Settings2, FileText, Bell, Activity, DollarSign, ExternalLink, Shield, FileSpreadsheet, Zap, Megaphone, GraduationCap } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { WasiAlertsModal } from '@/components/WasiAlertsModal';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/nuevo', icon: PlusCircle, label: 'Nuevo Registro' },
  { to: '/negocios', icon: Briefcase, label: 'Mis Negocios', deeplookOk: true },
  { to: '/estadisticas', icon: BarChart3, label: 'Estadisticas' },
  { to: '/agenda', icon: CalendarDays, label: 'Agenda y Tareas' },
  { to: '/seguimiento', icon: Building2, label: 'Seguimiento', adminOnly: true },
  { to: '/reporte', icon: FileBarChart, label: 'Reporte', adminOnly: true },
  { to: '/crm', icon: Users, label: 'CRM', deeplookOk: true },
  { to: '/crm-dashboard', icon: Activity, label: 'Dashboard CRM', deeplookOk: true },
  { to: '/formularios', icon: FileText, label: 'Formularios', adminOnly: true },
  { to: '/leads-curso', icon: GraduationCap, label: 'Leads Curso', adminOnly: true, deeplookOk: true },
  { to: '/crm-config', icon: Settings2, label: 'Config CRM', adminOnly: true },
  { to: '/meta', icon: Megaphone, label: 'Meta Ads', adminOnly: true },
  { to: '/finanzas', icon: DollarSign, label: 'Gestion Financiera', adminOnly: true },
  { to: '/links', icon: ExternalLink, label: 'Links Externos' },
  { to: '/inmuebles', icon: Building2, label: 'Inmuebles' },
  { to: '/habeas-data', icon: Shield, label: 'Habeas Data', adminOnly: true },
  { to: '/sheet-syncs', icon: FileSpreadsheet, label: 'Sync Sheets', adminOnly: true },
  { to: '/wasi', icon: Zap, label: 'Sync WASI', adminOnly: true },
  { to: '/usuarios', icon: Settings, label: 'Configuracion Usuarios', adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [notifModal, setNotifModal] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({ new_lead: true, pending_followup: true });
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [unassignedCount, setUnassignedCount] = useState(0);

  // Initialize push notifications
  usePushNotifications(user);

  // Poll unassigned leads count for admin (sidebar badge)
  useEffect(() => {
    if (user?.role !== 'admin') return;
    let stopped = false;
    const fetchCount = async () => {
      try {
        const r = await api.get('/crm/leads/unassigned');
        if (!stopped) setUnassignedCount(r.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const iv = setInterval(fetchCount, 30000);
    // Refresh when the CRM page emits an event after a bulk-assign
    const handler = () => fetchCount();
    window.addEventListener('selecto:unassigned-refresh', handler);
    return () => { stopped = true; clearInterval(iv); window.removeEventListener('selecto:unassigned-refresh', handler); };
  }, [user]);

  // Session heartbeat: ping every 60s while the tab is visible so we track real activity time
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const beat = async () => {
      if (stopped || document.visibilityState !== 'visible') return;
      try {
        const sid = sessionStorage.getItem('selecto_session_id');
        const r = await api.post('/auth/heartbeat', { session_id: sid });
        if (r?.session_id) sessionStorage.setItem('selecto_session_id', r.session_id);
      } catch { /* ignore */ }
    };
    beat();
    const iv = setInterval(beat, 60000);
    return () => { stopped = true; clearInterval(iv); };
  }, [user]);

  const handleEnableNotifications = async () => {
    if (!('Notification' in window)) return toast.error('Tu navegador no soporta notificaciones');
    // Load preferences and open modal
    try {
      const p = await api.get('/user/notification-prefs');
      setNotifPrefs(p);
    } catch { /* keep defaults */ }
    setNotifPerm(Notification.permission);
    setNotifModal(true);
    setSidebarOpen(false);
  };

  const requestBrowserPermission = async () => {
    const perm = await Notification.requestPermission();
    setNotifPerm(perm);
    if (perm === 'granted') {
      toast.success('Permiso otorgado');
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) window.location.reload();
      }
    } else {
      toast.error('Permiso denegado — actívalo desde la configuración del navegador');
    }
  };

  const saveNotifPrefs = async (partial) => {
    const next = { ...notifPrefs, ...partial };
    setNotifPrefs(next);
    try { await api.put('/user/notification-prefs', next); toast.success('Preferencias guardadas'); }
    catch { toast.error('Error al guardar'); }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.new) return toast.error('Completa todos los campos');
    if (passwordForm.new.length < 6) return toast.error('La nueva contraseña debe tener al menos 6 caracteres');
    if (passwordForm.new !== passwordForm.confirm) return toast.error('Las contraseñas no coinciden');
    setSaving(true);
    try {
      await api.put('/auth/change-password', { current_password: passwordForm.current, new_password: passwordForm.new });
      toast.success('Contraseña actualizada exitosamente');
      setPasswordModal(false);
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cambiar contraseña'); }
    setSaving(false);
  };

  const inputCls = "bg-[#0A1F1A] border-[#1A3B32] focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] h-11 text-white placeholder:text-white/30 rounded-xl";

  const filteredNav = navItems.filter(item => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'deeplook') return item.deeplookOk;
    return !item.adminOnly;
  });

  const SidebarContent = () => (
    <>
      <div className="p-6 lg:p-8 pb-4 lg:pb-6">
        <h1 className="font-outfit text-2xl font-bold tracking-tight text-white">SELECTO</h1>
        <p className="text-xs text-[#A38560] mt-1 tracking-[0.2em] uppercase font-medium">CRM Inmobiliario</p>
      </div>
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 lg:px-4 space-y-1 sidebar-scroll" data-testid="sidebar-nav">
        {filteredNav.map(item => (
          <NavLink key={item.to} to={item.to} end={item.end}
            onClick={() => setSidebarOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                isActive ? 'bg-[#A38560] text-[#03110D] shadow-lg shadow-[#A38560]/20' : 'text-white/60 hover:text-white hover:bg-white/5'
              }`
            }>
            <item.icon size={18} strokeWidth={1.5} />
            <span className="flex-1">{item.label}</span>
            {item.to === '/crm' && unassignedCount > 0 && (
              <span data-testid="nav-unassigned-badge"
                className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                {unassignedCount > 99 ? '99+' : unassignedCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 lg:p-6 border-t border-white/5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#A38560] to-[#8B6F47] flex items-center justify-center text-sm font-bold text-[#03110D] shrink-0">
            {user?.name?.charAt(0)}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate text-white">{user?.name}</p>
            <p className="text-xs text-white/40 capitalize">{user?.role}</p>
          </div>
        </div>
        <div className="space-y-1">
          <button onClick={handleEnableNotifications} data-testid="enable-notifications-btn"
            className="flex items-center gap-2 text-white/50 hover:text-[#A38560] text-sm transition-colors w-full py-2 px-2 rounded-lg hover:bg-white/5">
            <Bell size={16} strokeWidth={1.5} /> Notificaciones
          </button>
          <button onClick={() => { setPasswordModal(true); setSidebarOpen(false); }} data-testid="change-password-btn"
            className="flex items-center gap-2 text-white/50 hover:text-[#A38560] text-sm transition-colors w-full py-2 px-2 rounded-lg hover:bg-white/5">
            <Key size={16} strokeWidth={1.5} /> Cambiar Contraseña
          </button>
          <button onClick={handleLogout} data-testid="logout-btn"
            className="flex items-center gap-2 text-white/50 hover:text-[#A38560] text-sm transition-colors w-full py-2 px-2 rounded-lg hover:bg-white/5">
            <LogOut size={16} strokeWidth={1.5} /> Cerrar Sesion
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#03110D]" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-72 bg-[#03110D] text-white flex-col shrink-0 border-r border-white/5" data-testid="sidebar">
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#03110D] flex flex-col border-r border-white/5 animate-in slide-in-from-left duration-300">
            <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-white/50 hover:text-white p-1" data-testid="close-sidebar-btn">
              <X size={20} />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-[#03110D] border-b border-white/5 shrink-0">
          <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn" className="text-white p-1.5 rounded-lg hover:bg-white/5">
            <Menu size={22} />
          </button>
          <h1 className="font-outfit text-lg font-bold text-white tracking-tight">SELECTO</h1>
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#A38560] to-[#8B6F47] flex items-center justify-center text-xs font-bold text-[#03110D]">
            {user?.name?.charAt(0)}
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="p-4 lg:p-8 max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Change Password Modal */}
      {user?.role !== 'deeplook' && <WasiAlertsModal />}
      <Dialog open={passwordModal} onOpenChange={setPasswordModal}>
        <DialogContent className="sm:max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Key size={20} className="text-[#A38560]" /> Cambiar Mi Contraseña
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Contraseña Actual</Label>
              <Input type="password" value={passwordForm.current} onChange={e => setPasswordForm({ ...passwordForm, current: e.target.value })}
                placeholder="Tu contraseña actual" className={inputCls} data-testid="current-password-input" />
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Nueva Contraseña</Label>
              <Input type="password" value={passwordForm.new} onChange={e => setPasswordForm({ ...passwordForm, new: e.target.value })}
                placeholder="Minimo 6 caracteres" className={inputCls} data-testid="new-password-input-self" />
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Confirmar Nueva Contraseña</Label>
              <Input type="password" value={passwordForm.confirm} onChange={e => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                placeholder="Repite la nueva contraseña" className={inputCls} data-testid="confirm-password-input" />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <button onClick={() => { setPasswordModal(false); setPasswordForm({ current: '', new: '', confirm: '' }); }}
              className="px-4 py-2 text-sm font-medium text-white/50 hover:text-white transition-colors">Cancelar</button>
            <button onClick={handleChangePassword} disabled={saving} data-testid="confirm-change-pwd-btn"
              className="bg-[#A38560] hover:bg-[#8B6F47] text-[#03110D] font-medium px-4 py-2 rounded-md shadow-sm transition-all disabled:opacity-50">
              {saving ? 'Guardando...' : 'Cambiar Contraseña'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notifications Preferences Modal */}
      <Dialog open={notifModal} onOpenChange={setNotifModal}>
        <DialogContent className="sm:max-w-md mx-4 bg-[#0A1F1A] border-[#1A3B32] text-white" data-testid="notif-modal">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Bell size={20} className="text-[#A38560]" /> Notificaciones
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Browser permission status */}
            <div className={`p-3 rounded-xl border ${notifPerm === 'granted' ? 'bg-emerald-500/10 border-emerald-500/25' : 'bg-amber-500/10 border-amber-500/25'}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-white">
                    {notifPerm === 'granted' ? '✅ Permiso concedido' : notifPerm === 'denied' ? '⛔ Permiso denegado' : '⚠️ Permiso pendiente'}
                  </p>
                  <p className="text-[10px] text-white/50 mt-0.5">
                    {notifPerm === 'granted' ? 'Tu navegador puede mostrar notificaciones push.' :
                     notifPerm === 'denied' ? 'Actívalo desde la configuración del navegador (candado en la URL).' :
                     'Necesitas conceder el permiso para recibir avisos.'}
                  </p>
                </div>
                {notifPerm !== 'granted' && (
                  <button onClick={requestBrowserPermission} data-testid="request-notif-permission"
                    className="shrink-0 px-3 py-1.5 rounded-lg bg-[#A38560] text-[#03110D] text-xs font-semibold hover:bg-[#8B6F47]">
                    Activar
                  </button>
                )}
              </div>
            </div>

            <div className="pt-1">
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">¿Cuándo quieres que te avisemos?</p>
              <div className="space-y-2">
                <label className="flex items-start gap-3 p-3 rounded-xl bg-[#16302B] border border-white/5 hover:border-[#A38560]/30 cursor-pointer transition-colors" data-testid="notif-new-lead">
                  <input type="checkbox" checked={!!notifPrefs.new_lead}
                    onChange={e => saveNotifPrefs({ new_lead: e.target.checked })}
                    className="w-4 h-4 accent-[#A38560] mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-medium">Cuando llega un lead nuevo</p>
                    <p className="text-[11px] text-white/40 mt-0.5">Push cada vez que se cree o asigne un lead a ti (formulario, Meta Ads, Sheet Sync).</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 rounded-xl bg-[#16302B] border border-white/5 hover:border-[#A38560]/30 cursor-pointer transition-colors" data-testid="notif-pending-followup">
                  <input type="checkbox" checked={!!notifPrefs.pending_followup}
                    onChange={e => saveNotifPrefs({ pending_followup: e.target.checked })}
                    className="w-4 h-4 accent-[#A38560] mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-medium">Cuando tengo leads pendientes por seguimiento</p>
                    <p className="text-[11px] text-white/40 mt-0.5">Tareas que vencen hoy/mañana, pagos por cobrar y leads que requieren atención.</p>
                  </div>
                </label>
              </div>
              <p className="text-[10px] text-white/30 italic mt-3">
                Si desmarcas ambas, solo recibirás avisos generales del sistema. Para apagar todo, desactiva el permiso del navegador.
              </p>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setNotifModal(false)}
              className="bg-[#A38560] hover:bg-[#8B6F47] text-[#03110D] font-medium px-4 py-2 rounded-lg text-sm">
              Listo
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
